import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
} from '@mui/material';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import i18next from 'i18next';

import {
  CREATE_USER_GROUP_MUTATION,
  UPDATE_USER_GROUP_MUTATION,
  UPDATE_USER_GROUP_MEMBERS_MUTATION,
  GET_USER_GROUP_QUERY,
} from '../../pages/clients/gql';
import { PUBLIC_CLIENT_LIST_QUERY } from '../../pages/clients/gql';
import { dispatchException, dispatchMessage } from '../../helper/snackbar';

interface CreateEditUserGroupDialogProps {
  open: boolean;
  onClose: () => void;
  publicCompanyId: number;
  userGroup?: any;
}

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required(() => String(i18next.t('usergroup-error-name-required')))
    .max(30, () => String(i18next.t('usergroup-error-name-too-long'))),
});

/**
 * Dialog for creating or editing user groups.
 * Supports two-step process: basic info (name) and member selection.
 * Handles both creation and update workflows.
 */
export default function CreateEditUserGroupDialog({
  open,
  onClose,
  publicCompanyId,
  userGroup,
}: CreateEditUserGroupDialogProps) {
  const dispatch = useDispatch();
  const isEditing = !!userGroup;
  const [step, setStep] = useState<'basic' | 'members'>(isEditing ? 'basic' : 'basic');
  const [createdGroupId, setCreatedGroupId] = useState<number | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [showMembersSection, setShowMembersSection] = useState(isEditing);

  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: userGroup?.name || '',
    },
  });

  // Get existing members if editing
  const { data: groupData } = useQuery(GET_USER_GROUP_QUERY, {
    variables: { id: userGroup?.id },
    skip: !userGroup?.id,
    onCompleted: (data) => {
      if (data?.getUserGroup?.members) {
        setSelectedMembers(data.getUserGroup.members.map((m: any) => m.publicClientId));
      }
    },
  });

  // Get all clients for the company
  const { data: clientsData } = useQuery(PUBLIC_CLIENT_LIST_QUERY, {
    variables: {
      where: { publicCompanyId, isActive: true },
    },
    fetchPolicy: 'cache-and-network',
  });

  const [createUserGroup] = useMutation(CREATE_USER_GROUP_MUTATION);
  const [updateUserGroup] = useMutation(UPDATE_USER_GROUP_MUTATION);
  const [updateUserGroupMembers] = useMutation(UPDATE_USER_GROUP_MEMBERS_MUTATION);

  const onSubmitBasic = async (formData: any) => {
    try {
      if (isEditing) {
        // Bei Bearbeitung: Name updaten und dann Mitglieder updaten
        await updateUserGroup({
          variables: {
            input: {
              id: userGroup.id,
              name: formData.name,
            },
          },
        });
        
        // Mitglieder auch gleich updaten
        await updateUserGroupMembers({
          variables: {
            input: {
              userGroupId: userGroup.id,
              publicClientIds: selectedMembers,
            },
          },
        });
        
        dispatchMessage(dispatch, i18next.t('usergroup-updated-success'));
        onClose();
      } else {
        const result = await createUserGroup({
          variables: {
            input: {
              name: formData.name,
              publicCompanyId,
            },
          },
        });
        
        if (result.data?.createUserGroup) {
          setCreatedGroupId(result.data.createUserGroup.id);
          setShowMembersSection(true);
          dispatchMessage(dispatch, i18next.t('usergroup-created-success'));
          setStep('members');
        }
      }
    } catch (error: any) {
      dispatchException(dispatch, error);
    }
  };

  const onSubmitMembers = async () => {
    const groupId = isEditing ? userGroup.id : createdGroupId;
    if (!groupId) return;

    try {
      await updateUserGroupMembers({
        variables: {
          input: {
            userGroupId: groupId,
            publicClientIds: selectedMembers,
          },
        },
      });
      
      dispatchMessage(dispatch, i18next.t('usergroup-members-updated-success'));
      onClose();
    } catch (error: any) {
      dispatchException(dispatch, error);
    }
  };

  const handleMemberToggle = (clientId: number) => {
    setSelectedMembers(prev => 
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };


  const clients = clientsData?.listPublicClients?.clients || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? i18next.t('usergroup-dialog-edit-title') : 
         (step === 'basic' ? i18next.t('usergroup-dialog-title') : i18next.t('usergroup-members-title'))
        }
      </DialogTitle>
      
      <DialogContent>
        <Box component="form" sx={{ pt: 1 }}>
          {/* Name Eingabe - immer sichtbar */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {i18next.t('usergroup-name-placeholder')}
          </Typography>
          
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={i18next.t('usergroup-name-label')}
                error={!!errors.name}
                helperText={errors.name?.message as string}
                inputProps={{ maxLength: 30 }}
                sx={{ mb: 3 }}
              />
            )}
          />

          {/* Mitglieder Bereich - nur beim Bearbeiten oder nach dem Erstellen */}
          {(isEditing || showMembersSection) && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {i18next.t('usergroup-members-title')}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {i18next.t('usergroup-members-description')}
              </Typography>
              
              {clients.length === 0 ? (
                <Alert severity="info">{i18next.t('usergroup-no-active-contacts')}</Alert>
              ) : (
                <Grid container spacing={1}>
                  {clients.map((client: any) => (
                    <Grid item xs={12} sm={6} key={client.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedMembers.includes(client.id)}
                            onChange={() => handleMemberToggle(client.id)}
                          />
                        }
                        label={`${client.firstname} ${client.lastname} (${client.email})`}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          {i18next.t('usergroup-button-cancel')}
        </Button>
        
        {/* Bei Bearbeitung oder Erstellung */}
        {(step === 'basic' || isEditing) ? (
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmitBasic)}
          >
            {isEditing ? i18next.t('usergroup-button-update') : i18next.t('usergroup-button-create')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onSubmitMembers}
          >
            {i18next.t('save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}